import { makeStyles, IconButton, Box } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from "recoil";

import { MsinsLeadEntrySourceEnum } from "@/api/generated";
import { CustomIconButton } from "@/components/CustomIconButton";
import { LeadCheckModal } from "@/components/management/LeadCheckModal";
import { MsinsLeadModal } from "@/components/management/MsinsLeadModal";
import { OfferApproveModal } from "@/components/offer/ApproveModal";
import CustomPagination from "@/components/Pagination";
import { ManagedPropertyList } from "@/components/property/ManagedPropertyList";
import { PropertyEmptyState } from "@/components/property/PropertyEmptyState";
import { ErrorPanel } from "@/components/StatePanels";
import { Toast } from "@/components/Toast";
import { useDevice } from "@/hooks/useDevice";
import { usePostMsinsLead } from "@/hooks/usePostMsinsLead";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";
import { NavigationPath } from "@/utilities/enums";
import { calculatePagination } from "@/utilities/pagination";
import { getActualYieldRateByProperty } from "@/utilities/yield";
import { PropertyManagementListSnackbarState, SelectedPropertyId, SelectedPropertyIds } from "@/view_models/atoms";
import { PropertyIdToThisMonthlyBalanceSheet } from "@/view_models/balancesheetSelectors";
import { ConsiderationProperties } from "@/view_models/propertySelectors";

import MsinsBanner from "@assets/image/msinsBanner_con.png";

const useStyles = makeStyles({
  image: { width: "100%", objectFit: "contain" },
  floatingBanner: {
    position: "fixed",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    width: 400,
    maxWidth: "calc(100vw - 32px)",
    zIndex: 1000,
  },
  closeButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.grey[600],
    position: "absolute",
    top: -16,
    right: -16,
    width: 32,
    height: 32,
  },
  error: { padding: "32px 24px", margin: "auto", maxWidth: 560 },
});
export const ManagementConsiderationPage: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { isMobile } = useDevice();
  const [snackbarState, setSnackbarState] = useRecoilState(PropertyManagementListSnackbarState);

  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  const considerationProperties = useRecoilValue(ConsiderationProperties);
  const this_month_sheet_map_loadable = useRecoilValueLoadable(PropertyIdToThisMonthlyBalanceSheet(null));
  // ページネーション
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [showBanner, setShowBanner] = React.useState(true);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isMsinsLeadModalOpen, setIsMsinsLeadModalOpen] = useState(false);
  const [isMsinsCheckModalOpen, setIsMsinsCheckModalOpen] = useState(false);
  const { postMsinsLead } = usePostMsinsLead();
  const { enqueueSnackbar } = useSnackbar();

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const sheets = this_month_sheet_map_loadable;

  const onClickAddProperty = () => {
    // MEMO: 検討中の一棟物件が追加されるまでの暫定対応
    history.push("/add_new_property/consideration");
  };

  const onClickMsinsBanner = async () => {
    // Msins向けリードモーダルを表示
    setIsMsinsLeadModalOpen(true);
  };
  // Msins向けリードモーダルのボタンクリック時
  const onClickMsinsInterest = async () => {
    // Msins向けリードモーダルを非表示
    setIsMsinsLeadModalOpen(false);
    // Msins向け確認モーダルを表示
    setIsMsinsCheckModalOpen(true);
  };

  const onClickAccept = async () => {
    // Msins向けリードを登録し、承認時モーダルを表示
    const result = await postMsinsLead({
      source: MsinsLeadEntrySourceEnum.検討者向けバナー,
    });

    if (result.success) {
      // 承認時モーダル表示
      setIsApproveModalOpen(true);
    } else {
      // エラー処理
      console.error(result.error);
      enqueueSnackbar("ネットワークエラーです。しばらくたってからお試しください");
    }
    // Msins向け確認モーダルを非表示
    setIsMsinsCheckModalOpen(false);
  };

  // ページネーションのためのロジック
  const { currentProperties, totalPages } = calculatePagination(considerationProperties, currentPage);

  // 物件選択
  const onClickPropertyDetail = (propertyId: number) => {
    setSelectedPropertyId(propertyId);
    setSelectedPropertyIds([propertyId]);
    history.push("/management/consideration/detail");
  };

  return (
    <BaseTemplate
      headerTitle={isMobile ? "検討中物件" : undefined}
      navigationPath={NavigationPath.PROPERTY_MANAGEMENT_CONSIDERATION}
      isDense
      isPcV2
    >
      {sheets.state === "hasError" ? (
        <Box className={classes.error}>
          <ErrorPanel
            title="物件を読み込めませんでした"
            description={"通信状況を確認して、\nもう一度お試しください。"}
            retryLabel="再読み込み"
            onRetry={() => window.location.reload()}
          />
        </Box>
      ) : sheets.state !== "loading" && considerationProperties.length === 0 ? (
        <PropertyEmptyState type="consideration" onClick={onClickAddProperty} />
      ) : (
        <>
          <ManagedPropertyList
            properties={isMobile ? currentProperties : considerationProperties}
            count={considerationProperties.length}
            consideration
            loading={sheets.state === "loading"}
            getActualYield={(holder) =>
              getActualYieldRateByProperty(holder, {
                annualPropertyTax:
                  sheets.state === "hasValue"
                    ? sheets.contents.get(holder.id)?.result.property_tax_of_last_year
                    : undefined,
              })
            }
            onSelect={onClickPropertyDetail}
            onAdd={onClickAddProperty}
          >
            {(() => {
              const now = new Date(new Date().toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" }));
              return now >= new Date("2025-07-01T00:00:00+09:00") ? (
                isMobile ? (
                  <Box mb={1} position="relative">
                    <Box onClick={onClickMsinsBanner} style={{ cursor: "pointer" }}>
                      <img className={classes.image} src={MsinsBanner} alt="Msins consideration banner" />
                    </Box>
                  </Box>
                ) : (
                  showBanner && (
                    <Box className={classes.floatingBanner}>
                      <IconButton className={classes.closeButton} onClick={() => setShowBanner(false)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <Box onClick={onClickMsinsBanner} style={{ cursor: "pointer" }}>
                        <img className={classes.image} src={MsinsBanner} alt="Msins consideration banner" />
                      </Box>
                    </Box>
                  )
                )
              ) : null;
            })()}
          </ManagedPropertyList>
          {isMobile && sheets.state === "hasValue" && (
            <>
              <Box px={3} pb={2}>
                <CustomPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
              </Box>
              <CustomIconButton aria-label="物件を登録する" onClick={() => onClickAddProperty()} float>
                <AddIcon />
              </CustomIconButton>
            </>
          )}
        </>
      )}
      <Toast
        open={snackbarState.isOpen}
        variant={snackbarState.status}
        message={snackbarState.message}
        onClose={() => setSnackbarState({ ...snackbarState, isOpen: false })}
      />
      {isMsinsLeadModalOpen && (
        <MsinsLeadModal onClose={() => setIsMsinsLeadModalOpen(false)} onAccept={() => onClickMsinsInterest()} />
      )}
      {isMsinsCheckModalOpen && (
        <LeadCheckModal onClose={() => setIsMsinsCheckModalOpen(false)} onAccept={() => onClickAccept()} />
      )}
      {isApproveModalOpen && (
        <OfferApproveModal offerType="msins_consideration" onClose={() => setIsApproveModalOpen(false)} />
      )}
    </BaseTemplate>
  );
};
